'use client';

import { useMemo, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { freebiesService } from '@/lib/freebies';
import { emailService } from '@/lib/email';
import { subscriberLeadsService } from '@/lib/subscriberLeads';
import type { Freebie } from '@/types/freebie';

interface FreebieDetailClientProps {
  freebie: Freebie;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function FreebieDetailClient({ freebie }: FreebieDetailClientProps) {
  const [email, setEmail] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isValidEmail = useMemo(() => emailPattern.test(email.trim()), [email]);

  const trackRequest = async (action: 'download' | 'email') => {
    const normalizedEmail = email.trim();
    await Promise.all([
      freebiesService.submitRequest({
        freebieId: freebie.id || '',
        freebieName: freebie.name,
        email: normalizedEmail,
        action,
      }),
      subscriberLeadsService.create(normalizedEmail, 'freebie'),
    ]);
  };

  const ensureEmail = () => {
    if (!isValidEmail) {
      setMessage({ type: 'error', text: 'Enter a valid email to continue.' });
      return false;
    }
    return true;
  };

  const getDownloadFileName = () => {
    try {
      const url = new URL(freebie.fileUrl);
      const rawName = decodeURIComponent(url.pathname.split('/').pop() || '').replace(
        /^\d{13}-/,
        ''
      );
      if (rawName && /\.[a-z0-9]{2,10}$/i.test(rawName)) {
        return rawName;
      }
    } catch {
      // Ignore parsing errors and use fallback name.
    }

    const safeName = (freebie.slug || freebie.name || 'freebie')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-');
    return `${safeName || 'freebie'}-download`;
  };

  const triggerDirectDownload = async () => {
    const fileName = getDownloadFileName();

    let urlForDownload = freebie.fileUrl;
    try {
      const url = new URL(freebie.fileUrl);
      url.searchParams.set('response-content-disposition', `attachment; filename="${fileName}"`);
      urlForDownload = url.toString();
    } catch {
      // Keep original URL when parsing fails.
    }

    const response = await fetch(urlForDownload);
    if (!response.ok) {
      throw new Error(`Download request failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const handleDownload = async () => {
    if (!ensureEmail()) return;

    setIsBusy(true);
    setMessage(null);
    try {
      await trackRequest('download');
      await triggerDirectDownload();
      setMessage({ type: 'success', text: 'Download started successfully.' });
    } catch (error) {
      console.error('Error processing download request:', error);
      setMessage({ type: 'error', text: 'Could not start download. Please try again.' });
    } finally {
      setIsBusy(false);
    }
  };

  const handleSendEmail = async () => {
    if (!ensureEmail()) return;

    setIsBusy(true);
    setMessage(null);

    try {
      await trackRequest('email');
      const sent = await emailService.sendFreebieDeliveryEmail({
        recipientEmail: email.trim(),
        freebieName: freebie.name,
        freebieDescription: freebie.description,
        freebieImageUrl: freebie.imageUrl,
        freebieFileUrl: freebie.fileUrl,
      });

      if (!sent) {
        setMessage({ type: 'error', text: 'Email could not be sent right now. Please try again.' });
        return;
      }

      setMessage({
        type: 'success',
        text: "Your freebie is on its way to your inbox. If you don't see it in a few minutes, please check your Promotions or Spam folder as well.",
      });
    } catch (error) {
      console.error('Error sending freebie email:', error);
      setMessage({ type: 'error', text: 'Something went wrong while sending email.' });
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-10 md:py-14">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
        <div className="rounded-3xl border border-border bg-card overflow-hidden shadow-neutral">
          <div className="aspect-[4/3]">
            <AppImage
              src={freebie.imageUrl}
              alt={freebie.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6 md:p-8 shadow-neutral space-y-6">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground leading-tight">
            {freebie.name}
          </h1>

          <p className="text-muted-foreground leading-relaxed text-base md:text-lg">
            {freebie.description}
          </p>

          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              className="w-full rounded-xl border border-input bg-background px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground">
              Enter your email to unlock download or receive this freebie via email.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleSendEmail}
              disabled={isBusy}
              className="w-full rounded-xl bg-primary px-5 py-3 text-primary-foreground font-semibold disabled:opacity-50"
            >
              {isBusy ? 'Sending...' : 'Send to Email'}
            </button>
          </div>

          {message && (
            <div
              className={`rounded-xl px-4 py-3 text-sm font-medium ${
                message.type === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="pt-3 border-t border-border text-sm text-muted-foreground flex items-center gap-2">
            <Icon name="ShieldCheckIcon" size={16} className="text-primary" />
            Your email is used only for freebie delivery and updates you request.
          </div>
        </div>
      </div>
    </div>
  );
}
